if (!customElements.get('product-form')) {
  class ProductForm extends HTMLElement {
    constructor() {
      super();
      if (this.hasChildNodes()) this.init();
    }

    init() {
      this.form = this.querySelector('.js-product-form');
      if (this.form) {
        this.cartDrawer = document.querySelector('cart-drawer');

        if (theme.settings.afterAtc !== 'no-js' || this.form.dataset.t26PurchaseBlocked === 'true') {
          this.form.addEventListener('submit', this.handleSubmit.bind(this));
        }
      }
    }

    /**
     * Handles submission of the product form.
     * @param {object} evt - Event object.
     */
    async handleSubmit(evt) {
      if (evt.target.id === 'product-signup_form') return;

      evt.preventDefault();

      // T26: Keep the variant form available for browsing, without allowing a held product to submit.
      if (this.form.dataset.t26PurchaseBlocked === 'true') return;

      this.submitBtn = this.querySelector('[name="add"]');

      if (!this.submitBtn || this.submitBtn.disabled || this.form.dataset.t26VariantPending || this.submitBtn.getAttribute('aria-disabled') === 'true') return;
      if (theme.t26CartMutation && !theme.t26CartMutation.begin(this)) {
        this.cartDrawer?.open(this.submitBtn);
        return;
      }

      if (theme.settings.vibrateOnATC && window.navigator.vibrate) {
        window.navigator.vibrate(100);
      }

      this.errorMsg = null;
      this.setErrorMsgState();

      // T26: Show progress now; success is only known after Shopify confirms it.
      if (this.resetTimeout) clearTimeout(this.resetTimeout);
      this.submitBtn.setAttribute('aria-disabled', 'true');
      this.submitBtn.classList.add('is-loading');

      const formData = new FormData(this.form);
      const variantId = Number(formData.get('id'));
      let itemInOldCart = ProductForm.getCartItem(variantId);
      const t26OldLines = Array.from(document.querySelectorAll('cart-items .cart-item')).map((item) => ({
        key: item.dataset.lineKey, quantity: Number(item.querySelector('.qty-input__input')?.value || 0)
      }));
      const quickAddDrawer = this.closest('quick-add-drawer');
      let sections = 'cart-icon-bubble,cart-json';
      if (this.cartDrawer) {
        sections += `,${this.cartDrawer.closest('.shopify-section').id.replace('shopify-section-', '')}`;
      }

      formData.append('sections_url', window.location.pathname);
      formData.append('sections', sections);

      const fetchRequestOpts = {
        method: 'POST',
        headers: {
          Accept: 'application/javascript',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: formData
      };
      let itemAdded = false;
      let t26Announced = false;
      let t26AnnounceCart;
      let t26Unresolved = false;
      let t26DrawerError = '';
      const t26PendingDrawer = Boolean(this.cartDrawer?.t26BeginAdd && !quickAddDrawer && theme.settings.afterAtc === 'drawer');

      try {
        if (t26PendingDrawer) this.cartDrawer.t26BeginAdd(this.submitBtn);

        const response = await fetch(theme.routes.cartAdd, fetchRequestOpts);
        const data = await response.json();
        let error = typeof data.description === 'string' ? data.description : data.message;
        if (data.errors && typeof data.errors === 'object') {
          error = Object.entries(data.errors).map((item) => item[1].join(', '));
        }

        if (data.status) this.setErrorMsgState(error);

        if (!response.ok) throw new Error(response.status);
        itemAdded = true;
        if (data.key && t26OldLines.length) itemInOldCart = t26OldLines.find((item) => item.key === data.key);

        let cartEventName;
        let cartEventDetail;
        if (itemInOldCart) {
          cartEventName = 'on:line-item:change';
          cartEventDetail = {
            variantId: data.variant_id,
            oldQuantity: itemInOldCart.quantity,
            newQuantity: (itemInOldCart.quantity === data.quantity)
              ? itemInOldCart.quantity : data.quantity
          };
        } else {
          cartEventName = 'on:cart:add';
          cartEventDetail = {
            variantId: data.variant_id
          };
        }

        t26AnnounceCart = (cart) => {
          ProductForm.updateCartStateFromCart(cart);
          this.dispatchCartEvent(cartEventName, cartEventDetail, cart);
          t26Announced = true;
        };
        let cartData = ProductForm.getCartFromSections(data);
        if (!cartData) {
          const cartResponse = await fetch(`${theme.routes.cart}.js`);
          if (!cartResponse.ok) throw new Error('Cart state unavailable');
          cartData = await cartResponse.json();
        }
        if (!Array.isArray(cartData?.items)) throw new Error('Invalid cart state');
        t26AnnounceCart(cartData);
        this.submitBtn.classList.add('is-success');

        if (theme.settings.afterAtc === 'page') {
          // Allow the tick animation to complete
          setTimeout(() => {
            window.location.href = theme.routes.cart;
          }, 300);
        } else {
          // Update cart icon count.
          ProductForm.updateCartIcon(data);

          // If item was added from Quick Add drawer, show "Added to cart" message.
          if (quickAddDrawer) quickAddDrawer.addedToCart();

          // Update cart drawer contents immediately; optional controls enhance after it is visible.
          if (this.cartDrawer) {
            this.cartDrawer.renderContents(
              data,
              !t26PendingDrawer && !quickAddDrawer && theme.settings.afterAtc === 'drawer'
            );
          } else if (window.location.pathname === theme.routes.cart) {
            const pageCartItems = document.querySelector('cart-items');
            if (pageCartItems) {
              if (pageCartItems.dataset.empty === 'true') {
                window.location.reload();
              } else {
                await pageCartItems.refresh(this);
              }
            }
          }
        }
      } catch (error) {
        console.log(error); // eslint-disable-line
        if (this.cartDrawer?.t26RecoverCart) {
          try {
            const cart = await this.cartDrawer.t26RecoverCart();
            if (itemAdded && !t26Announced) t26AnnounceCart?.(cart);
          } catch (_) {
            t26Unresolved = true;
          }
          if (!itemAdded || t26Unresolved) {
            t26DrawerError = itemAdded ? this.cartDrawer.dataset.t26CartAddedError : this.cartDrawer.dataset.t26AddError;
          }
        } else if (this.cartDrawer) {
          await this.cartDrawer.refresh(true);
        }
        if (itemAdded && !this.cartDrawer) window.location.href = theme.routes.cart;
        if (!itemAdded) {
          if (!this.errorMsg?.textContent) this.setErrorMsgState(t26DrawerError || theme.strings.cartError);
          this.dispatchEvent(new CustomEvent('on:cart:error', {
            bubbles: true,
            detail: { error: this.errorMsg?.textContent }
          }));
        }
      } finally {
        if (!t26Unresolved) theme.t26CartMutation?.end(this);
        this.cartDrawer?.t26FinishAdd?.(t26DrawerError, t26Unresolved);
        // Re-enable 'Add to Cart' button.
        this.submitBtn.removeAttribute('aria-disabled');
        const resetButton = () => {
          this.submitBtn.classList.remove('is-loading');
          this.submitBtn.classList.remove('is-success');
        };

        if (itemAdded && theme.settings.afterAtc === 'page') {
          this.resetTimeout = setTimeout(() => {
            resetButton();
          }, 300);
        } else {
          resetButton();
        }
      }
    }

    /**
     * Returns the current cart entry for a variant without a network request.
     * @param {number} variantId - Variant ID.
     * @returns {object|undefined}
     */
    static getCartItem(variantId) {
      const renderedItem = Array.from(document.querySelectorAll('cart-items .cart-item'))
        .find((item) => Number(item.dataset.variantId) === variantId);
      const renderedQuantity = renderedItem?.querySelector('.qty-input__input');
      if (renderedQuantity) {
        return {
          variant_id: variantId,
          quantity: Number(renderedQuantity.value)
        };
      }

      const cartItem = theme.cart?.items?.find((item) => Number(item.variantId) === variantId);
      if (!cartItem) return undefined;

      return {
        variant_id: variantId,
        quantity: Number(cartItem.quantity)
      };
    }

    /**
     * Extracts the full cart object bundled with the Add response.
     * @param {object} response - Cart Add response with rendered sections.
     * @returns {object|null}
     */
    static getCartFromSections(response) {
      const html = response.sections?.['cart-json'];
      if (typeof html !== 'string') return null;

      const template = document.createElement('template');
      template.innerHTML = html;
      const cartState = template.content.querySelector('[data-cart-state]');
      if (!cartState) return null;

      try {
        return JSON.parse(cartState.textContent);
      } catch (error) {
        console.warn('Unable to parse bundled cart state.', error); // eslint-disable-line
        return null;
      }
    }

    /**
     * Replaces the lightweight local cart snapshot from a full Cart Ajax API object.
     * @param {object} cart - Current cart object.
     */
    static updateCartStateFromCart(cart) {
      theme.cart = theme.cart || { items: [] };
      theme.cart.items = cart.items.map((item) => ({
        variantId: item.variant_id,
        quantity: item.quantity
      }));
    }

    /**
     * Dispatches an Enterprise cart event with its documented full cart payload.
     * @param {string} eventName - Custom event name.
     * @param {object} detail - Event-specific detail.
     * @param {object} cart - Current cart object.
     */
    dispatchCartEvent(eventName, detail, cart) {
      this.dispatchEvent(new CustomEvent(eventName, {
        bubbles: true,
        detail: {
          cart,
          ...detail
        }
      }));
    }

    /**
     * Updates the cart icon count in the header.
     * @param {object} response - Response JSON.
     */
    static updateCartIcon(response) {
      const cartIconBubble = document.getElementById('cart-icon-bubble');
      if (cartIconBubble) {
        cartIconBubble.innerHTML = response.sections['cart-icon-bubble'];
      }
    }

    /**
     * Shows/hides an error message.
     * @param {string} [error=false] - Error to show a message for.
     */
    setErrorMsgState(error = false) {
      this.errorMsg = this.errorMsg || this.querySelector('.js-form-error');
      if (!this.errorMsg) return;

      this.errorMsg.hidden = !error;
      if (error) {
        this.errorMsg.innerHTML = '';
        const errorArray = Array.isArray(error) ? error : [error];
        errorArray.forEach((err, index) => {
          if (index > 0) this.errorMsg.insertAdjacentHTML('beforeend', '<br>');
          this.errorMsg.insertAdjacentText('beforeend', err);
        });
      }
    }
  }

  customElements.define('product-form', ProductForm);
}
